import { useState, useContext, useRef } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Sidebar from './Sidebar.js';
import CountryVisaStay from './CountryVisaStay';
import { Card, Row, Col, Container, Alert, Image } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { useNavigate } from 'react-router-dom';
import { validateURL} from './utils/validation';
import Breadcrumb from './Breadcrumb.js';
import CountryAddGeneral from './CountryAddGeneral';
import CountryWorkTax from './CountryWorkTax';
import CountryFinancialsBanks from './CountryFinancialsBanks';
import CountryHousingLife from './CountryHousingLife';
import CountrySocial from './CountrySocial';
import CountryIndustries from './CountryIndustries';
import { isAdminType} from './utils/roleUtils';
import { TicketCountContext } from './TicketCountContext';
import currencies from './data/currencies'
import icSucess from './assets/img/ic-celebration-circle.svg';

const CountryAdd = () => {
    const { user } = useContext(UserContext);
    const [step, setStep] = useState(1); // Step state
    const navigate = useNavigate(); // Initialize useNavigate
    const [formData, setFormData] = useState({
        country: { id: '', name: '' },
        url: '',
        citizensNumber: '',
        languagesSpoken: [], // ✅ must be array for isMulti
        currency: '',
        continent: '',
        industries: [], // ✅ prevent JSON.parse(undefined) in CountryIndustries
    });

    const [errors, setErrors] = useState({}); // State for validation errors
    const { ticketCount} = useContext(TicketCountContext);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    
    const housingLifeRef = useRef(null);

    const generalRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target || e;
        console.log('handleChange called with:', name, value);

        // ✅ Country is an object: { id, name }
        if (name === 'country') {
            setFormData(prevData => ({
                ...prevData,
                country: value && typeof value === 'object'
                    ? { ...value }
                    : { ...(prevData.country || {}), id: value }
            }));
            return;
        }

        // ✅ Default scalar fields
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const validateStep1 = () => {
        const newErrors = {};

        // Country name
        if (!formData.country?.name) {
            newErrors.name = 'Country name is required.';
        }

        // Country URL
        if (!String(formData.url || '').trim()) {
            newErrors.url = 'Country website is required.';
        } else if (!validateURL(formData.url)) {
            newErrors.url = 'Please enter a valid URL.';
        }

        // Continent
        if (!String(formData.continent ?? '').trim()) {
            newErrors.continent = 'Continent is required.';
        }

        // Currency (and ensure it exists in the imported list)
        const allowedCurrencies = new Set((currencies || []).map(x => x?.value ?? x));
        if (!String(formData.currency ?? '').trim()) {
            newErrors.currency = 'Currency is required.';
        } else if (!allowedCurrencies.has(formData.currency)) {
            newErrors.currency = 'Please pick a valid currency.';
        }

        // Citizens number: required, non-negative integer
        const n = Number(formData.citizensNumber);
        if (formData.citizensNumber === '' || formData.citizensNumber == null) {
            newErrors.citizensNumber = 'Number of citizens is required.';
        } else if (Number.isNaN(n)) {
            newErrors.citizensNumber = 'Enter a valid number.';
        } else if (n < 0) {
            newErrors.citizensNumber = 'Value cannot be negative.';
        } else if (!Number.isInteger(n)) {
            newErrors.citizensNumber = 'Enter a whole number.';
        }

        // Languages (ask child to set its own touched + localErrors)
        const okLangs = generalRef.current?.validateLanguagesSpoken?.() ?? false;
        if (!okLangs) {
            newErrors.languagesSpoken = 'Select at least one language';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0 && okLangs;
    };
    
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await patchStep(7);
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error patching Step 7:', error);
            alert('An error occurred while saving. Please try again.');
        }
    };
    

    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    const patchStep = async (stepToSave) => {
        if (!formData?._id) return;

        let payload = {};

        if (stepToSave === 2) {
            payload = {
                visaType: formData.visaType ?? '',
                noPermitPeriod: formData.noPermitPeriod ?? '',
            };
        }

        if (stepToSave === 3) {
            payload = {
                weekWorkingHours: formData.weekWorkingHours ?? '',
                minimumWage: formData.minimumWage ?? '',
                wageAmount: formData.wageAmount ?? '',
                internshipsRegulation: formData.internshipsRegulation ?? '',
                incomeTaxLiability: formData.incomeTaxLiability ?? '',
                doubleTaxationAgreementUrl: formData.doubleTaxationAgreementUrl ?? '',
                healthInsurance: formData.healthInsurance ?? '',
                accidentInsurance: formData.accidentInsurance ?? '',
            };
        }

        if (stepToSave === 4) {
            payload = {
                bankAccountDocuments: formData.bankAccountDocuments ?? '',
                exchangeRate: formData.exchangeRate ?? '',
            };
        }

        if (stepToSave === 5) {
            payload = {
                platformUrls: formData.platformUrls ?? [],
                averageRentalPricesUrban: formData.averageRentalPricesUrban ?? '',
                averageRentalPricesRural: formData.averageRentalPricesRural ?? '',
                transportMonthly: formData.transportMonthly ?? '',
                livingCosts: formData.livingCosts ?? '',
                rentingRestrictions: formData.rentingRestrictions ?? '',
                rentingRestrictionsText: formData.rentingRestrictionsText ?? '',
            };
        }

        if (stepToSave === 6) {
            payload = {
                importantLanguages: formData.importantLanguages ?? [],
                englishWide: formData.englishWide ?? '',
                socialMediaPlatforms: formData.socialMediaPlatforms ?? [],
                jobPlatforms: formData.jobPlatforms ?? [],
            };
        }

        if (stepToSave === 7) {
            payload = {
                industries: formData.industries ?? [],
            };
        }

        if (!payload || Object.keys(payload).length === 0) return;

        await api.patch(`/api/countries/${formData._id}`, payload);
    };


    const handleNextStep = async () => {
        if (step === 1) {
            if (!validateStep1()) {
                console.log('Validation failed:', errors);
                setShowErrorMessage(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
    
            try {
                const response = await api.post(
                    `/api/countries`,
                    formData
                );
             
                console.log('Sending data:', formData);
                console.log('Country created:', response.data);
                setFormData((prevData) => ({
                    ...prevData,
                    _id: response.data._id,
                }));
                setShowErrorMessage(false);
                setStep(2); // ✅ Move to Step 2 (Visa & Stay)
            } catch (error) {
                console.error('Error creating country:', error);
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    general: error.response?.data?.message || 'An error occurred while creating the country. Please try again.',
                }));
            }
        } else if (step === 2) {
            try {
                await patchStep(2); // ✅ PATCH visa step fields only
                setShowErrorMessage(false);
                setStep(3);
            } catch (error) {
                console.error('Error patching Step 2:', error);
                setShowErrorMessage(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        } else if (step === 3) {
            try { await patchStep(3); } catch (e) { console.error('Error patching Step 3:', e); }
            setStep(4);
        } else if (step === 4) {
            try { await patchStep(4); } catch (e) { console.error('Error patching Step 4:', e); }
            setStep(5);
        } else if (step === 5) {
            const okHousing = housingLifeRef.current?.validateHousingLife?.() ?? false;
            if (!okHousing) {
                setShowErrorMessage(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            try { await patchStep(5); } catch (e) { console.error('Error patching Step 5:', e); }
            setStep(6);
        } else if (step === 6) {
            try { await patchStep(6); } catch (e) { console.error('Error patching Step 6:', e); }
            setStep(7);
        }
    };
    
    const handlePreviousStep = () => setStep((prevStep) => prevStep - 1);
    
    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        {/* Main Content */}
                        <Col xs={12}>
                            <Card>
                                <Card.Body>
                                    <h1>Add New Country</h1>
                                    <Form onSubmit={step === 2 ? handleSubmit : undefined}>
                                        {step === 1 && (
                                            <CountryAddGeneral
                                                ref={generalRef}
                                                formData={formData}
                                                handleChange={handleChange}
                                                errors={errors}
                                                validateURL={validateURL}
                                            />
                                        )}

                                        {step === 2 && <CountryVisaStay formData={formData} handleChange={handleChange} />}
                                        {step === 3 && <CountryWorkTax formData={formData} handleChange={handleChange} />}
                                        {step === 4 && <CountryFinancialsBanks formData={formData} handleChange={handleChange} />}
                                        {step === 5 && 
                                            <CountryHousingLife
                                                ref={housingLifeRef}
                                                formData={formData}
                                                errors={errors}
                                                handleChange={handleChange}
                                           />
                                        }
                                        {step === 6 && 
                                            <CountrySocial
                                                formData={formData}
                                                handleChange={handleChange}
                                                validateURL={validateURL}
                                            />
                                        }
                                        {step === 7 && <CountryIndustries formData={formData} handleChange={handleChange} />}
                                        
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col md={6}>
                            <p>All fields marked with an asterisk (*) are mandatory and must be completed.</p>
                        </Col>
                        <Col className="justify-content-end d-flex align-items-center" md={6}>
                            <Button className="me-3" variant="outline-secondary" onClick={() => navigate('/onboarding-country-list')}>
                                Cancel
                            </Button>
                            {step > 1 && <Button className="me-3" variant="outline-secondary" onClick={handlePreviousStep}>Back</Button>}
                            {step === 1 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 2 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 3 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 4 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 5 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 6 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 7 && <Button variant="primary" onClick={handleSubmit}>Finish</Button>}
                        </Col>
                    </Row>
                </Container>
            </main>
                                    
            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Congratulations</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You have successfully added a new country to aBROADIFY.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={() => navigate('/onboarding-country-list')}>
                        Go to Countries
                    </Button>
                </Modal.Footer>
            </Modal>;

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Country Added</h2>
                    <p className="text-center">You have successfully added a new country to aBROADIFY</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button variant="primary" onClick={() => navigate('/onboarding-country-list')}>
                        Go back to countries
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CountryAdd;
